import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/hr`;
interface Emp { id: number; first_name: string; last_name: string; email: string; department: string; job_title: string; annual_salary: number | null; lifecycle_status: string; version: number; location: string; }
interface Summary { total: number; active: number; total_salary: number; by_department: Record<string, number>; }
interface Leave { id: number; employee_id: number; leave_type: string; start_date: string; end_date: string; days_requested: number; status: string; }
interface Review { id: number; employee_id: number; review_cycle: string; rating: number | null; goals_met_pct: number | null; status: string; }
interface HeadcountRow { department: string; fiscal_year: number; quarter: number | null; planned: number; actual: number; open: number; budget: number; }

export default function HRPeople() {
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const navigate = useNavigate();
  const [emps, setEmps] = useState<Emp[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'employees' | 'leave' | 'reviews' | 'headcount' | 'orgchart'>('employees');
  const [showCreate, setShowCreate] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ entity_code: '', first_name: '', last_name: '', email: '', department: '', job_title: '', annual_salary: '' });
  const [leaveForm, setLeaveForm] = useState({ employee_id: '', leave_type: 'PTO', start_date: '', end_date: '', days_requested: '1', reason: '' });
  const [reviewForm, setReviewForm] = useState({ employee_id: '', review_cycle: 'ANNUAL', review_period_start: '', review_period_end: '' });

  // Tab data
  const [reviews, setReviews] = useState<Review[]>([]);
  const [headcount, setHeadcount] = useState<HeadcountRow[]>([]);
  const [orgChart, setOrgChart] = useState<Record<string, Emp[]>>({});

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, sRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/employees?limit=100`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (eRes.ok) { const d = await eRes.json(); setEmps(d.items || []); }
      if (sRes.ok) setSummary(await sRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const loadTab = async (t: string) => {
    setTab(t as any);
    if (t === 'reviews') {
      const res = await authFetch(withEntityParam(`${API}/reviews`, entity));
      if (res.ok) { const d = await res.json(); setReviews(d.reviews || []); }
    } else if (t === 'headcount') {
      const res = await authFetch(withEntityParam(`${API}/headcount`, entity));
      if (res.ok) { const d = await res.json(); setHeadcount(d.plans || []); }
    } else if (t === 'orgchart') {
      const res = await authFetch(withEntityParam(`${API}/org-chart`, entity));
      if (res.ok) { const d = await res.json(); setOrgChart(d.by_manager || {}); }
    }
  };

  const handleCreate = async () => {
    const payload = { ...form, annual_salary: form.annual_salary ? parseFloat(form.annual_salary) : null };
    const res = await authFetch(`${API}/employees`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Employee created', true); setShowCreate(false); setForm({ entity_code: '', first_name: '', last_name: '', email: '', department: '', job_title: '', annual_salary: '' }); load(); }
    else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const handleAction = async (id: number, action: string, version: number) => {
    const res = await authFetch(`${API}/employees/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
    if (res.ok) { showToast(`Employee ${action}d`, true); load(); } else { const e = await res.json(); showToast(e.detail || 'Failed', false); }
  };

  const handleLeave = async () => {
    const empId = parseInt(leaveForm.employee_id);
    if (!empId) { showToast('Select an employee', false); return; }
    const payload = { leave_type: leaveForm.leave_type, start_date: leaveForm.start_date, end_date: leaveForm.end_date, days_requested: parseFloat(leaveForm.days_requested) || 1, reason: leaveForm.reason };
    const res = await authFetch(`${API}/employees/${empId}/leave`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Leave requested', true); setShowLeave(false); } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const handleLeaveAction = async (id: number, action: string) => {
    const res = await authFetch(`${API}/leave/${id}/${action}`, { method: 'POST' });
    if (res.ok) { showToast(`Leave ${action}d`, true); } else { const e = await res.json(); showToast(e.detail || 'Failed', false); }
  };

  const handleReview = async () => {
    const payload = { employee_id: parseInt(reviewForm.employee_id), review_cycle: reviewForm.review_cycle, review_period_start: reviewForm.review_period_start, review_period_end: reviewForm.review_period_end };
    const res = await authFetch(`${API}/reviews`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Review created', true); setShowReview(false); loadTab('reviews'); } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const handleTabChange = (key: string) => {
    if (key === 'employees') setTab('employees');
    else loadTab(key);
  };

  const getPageActions = () => {
    if (tab === 'employees') return [{ label: '+ New Employee', onClick: () => setShowCreate(true) }];
    if (tab === 'leave') return [{ label: '+ Request Leave', onClick: () => setShowLeave(true) }];
    if (tab === 'reviews') return [{ label: '+ New Review', onClick: () => setShowReview(true) }];
    return [];
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const empColumns: Column[] = [
    { key: 'name', label: 'Name', render: (_v: any, row: any) => <span style={{ fontWeight: 600 }}>{row.first_name} {row.last_name}</span> },
    { key: 'email', label: 'Email', render: (v: any) => v || '-' },
    { key: 'department', label: 'Department', render: (v: any) => v || '-' },
    { key: 'job_title', label: 'Title', render: (v: any) => v || '-' },
    { key: 'annual_salary', label: 'Salary', align: 'right', render: (v: any) => v != null ? formatAmount(v) : '-' },
    { key: 'lifecycle_status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'actions', label: 'Actions', render: (_v: any, row: any) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <ActionButton label="View" onClick={(e) => { e.stopPropagation(); navigate(`/hr/employees/${row.id}`); }} variant="secondary" size="sm" />
        {row.lifecycle_status === 'ONBOARDING' && <ActionButton label="Activate" onClick={(e) => { e.stopPropagation(); handleAction(row.id, 'activate', row.version); }} variant="success" size="sm" />}
        {row.lifecycle_status === 'ACTIVE' && <ActionButton label="Terminate" onClick={(e) => { e.stopPropagation(); handleAction(row.id, 'terminate', row.version); }} variant="danger" size="sm" />}
      </div>
    )},
  ];

  const reviewColumns: Column[] = [
    { key: 'employee_id', label: 'Employee', render: (v: any) => `Employee #${v}` },
    { key: 'review_cycle', label: 'Cycle' },
    { key: 'rating', label: 'Rating', render: (v: any) => <span style={{ fontWeight: 600 }}>{v != null ? `${v}/5` : '-'}</span> },
    { key: 'goals_met_pct', label: 'Goals %', render: (v: any) => v != null ? `${v}%` : '-' },
    { key: 'status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
  ];

  const headcountColumns: Column[] = [
    { key: 'department', label: 'Department', render: (v: any) => <span style={{ fontWeight: 600 }}>{v || '-'}</span> },
    { key: 'fiscal_year', label: 'Year' },
    { key: 'planned', label: 'Planned', align: 'right' },
    { key: 'actual', label: 'Actual', align: 'right', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'open', label: 'Open', align: 'right', render: (v: any) => <span style={{ color: v > 0 ? 'var(--warning)' : 'var(--positive)' }}>{v}</span> },
    { key: 'budget', label: 'Budget', align: 'right', render: (v: any) => formatAmount(v) },
  ];

  return (
    <PageLayout title="HR / People" subtitle="Employees, leave, performance reviews, headcount planning, and org chart" actions={getPageActions()}>
      {summary && (
        <KpiGrid items={[
          { label: 'Total', value: summary.total },
          { label: 'Active', value: summary.active, color: 'var(--positive)' },
          { label: 'Total Salary', value: formatAmountCompact(summary.total_salary), color: 'var(--accent)' },
          { label: 'Departments', value: Object.keys(summary.by_department).length },
        ]} columns={4} />
      )}

      <TabBar
        tabs={[
          { key: 'employees', label: 'Employees' },
          { key: 'leave', label: 'Leave' },
          { key: 'reviews', label: 'Reviews' },
          { key: 'headcount', label: 'Headcount' },
          { key: 'orgchart', label: 'Org Chart' },
        ]}
        active={tab}
        onChange={handleTabChange}
      />

      {tab === 'employees' ? (
        <DataTable columns={empColumns} rows={emps} loading={loading && tab === 'employees'} emptyMessage="No employees." rowKey="id" />
      ) :

      tab === 'leave' ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Select an employee to view leave balance:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {emps.map(e => (
              <ActionButton key={e.id} label={`${e.first_name} ${e.last_name}`} variant="secondary" size="sm" onClick={async () => {
                const res = await authFetch(`${API}/employees/${e.id}/leave-balance`);
                if (res.ok) { const d = await res.json(); showToast(`${e.first_name}: ${d.balances.length} balance(s)`, true); }
              }} />
            ))}
          </div>
          {emps.length === 0 && <div style={{ color: 'var(--text-secondary)', marginTop: 16 }}>Create employees first.</div>}
        </div>
      ) :

      tab === 'reviews' ? (
        <DataTable columns={reviewColumns} rows={reviews} emptyMessage='No performance reviews. Click "+ New Review" to start.' rowKey="id" />
      ) :

      tab === 'headcount' ? (
        <DataTable columns={headcountColumns} rows={headcount} emptyMessage="No headcount plans." />
      ) :

      tab === 'orgchart' ? (
        Object.keys(orgChart).length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>No org chart data. Add employees with manager assignments.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Object.entries(orgChart).map(([mgrId, reports]) => (
              <div key={mgrId} style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>Manager #{mgrId === '0' ? 'None (top level)' : mgrId} <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 400 }}>({reports.length} reports)</span></div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {reports.map((r: any) => (
                    <div key={r.id} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 11 }}>
                      <div style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{r.job_title || r.department || '-'}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {/* Create Employee Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Employee" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} — {e.entity_name} ({e.functional_currency})</option>)}
          </select>
        <div style={{ display: 'flex', gap: 8 }}><input placeholder="First Name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} style={inputStyle} /><input placeholder="Last Name *" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} style={inputStyle} /></div><input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /><div style={{ display: 'flex', gap: 8 }}><input placeholder="Department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} style={inputStyle} /><input placeholder="Job Title" value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} style={inputStyle} /></div><input placeholder="Annual Salary" type="number" value={form.annual_salary} onChange={e => setForm({ ...form, annual_salary: e.target.value })} style={inputStyle} /></div>
      </Modal>

      {/* Leave Request Modal */}
      <Modal open={showLeave} onClose={() => setShowLeave(false)} title="Request Leave" footer={<><ActionButton label="Cancel" onClick={() => setShowLeave(false)} variant="secondary" /><ActionButton label="Submit" onClick={handleLeave} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={leaveForm.employee_id} onChange={e => setLeaveForm({ ...leaveForm, employee_id: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Employee</option>
            {emps.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
          </select>
          <select value={leaveForm.leave_type} onChange={e => setLeaveForm({ ...leaveForm, leave_type: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            {['PTO', 'SICK', 'PARENTAL', 'SABBATICAL', 'UNPAID', 'BEREAVEMENT'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Start</label><input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({ ...leaveForm, start_date: e.target.value })} style={inputStyle} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>End</label><input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({ ...leaveForm, end_date: e.target.value })} style={inputStyle} /></div>
          </div>
          <input placeholder="Days" type="number" value={leaveForm.days_requested} onChange={e => setLeaveForm({ ...leaveForm, days_requested: e.target.value })} style={inputStyle} />
          <input placeholder="Reason" value={leaveForm.reason} onChange={e => setLeaveForm({ ...leaveForm, reason: e.target.value })} style={inputStyle} />
        </div>
      </Modal>

      {/* Review Modal */}
      <Modal open={showReview} onClose={() => setShowReview(false)} title="New Performance Review" footer={<><ActionButton label="Cancel" onClick={() => setShowReview(false)} variant="secondary" /><ActionButton label="Create" onClick={handleReview} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={reviewForm.employee_id} onChange={e => setReviewForm({ ...reviewForm, employee_id: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Employee</option>
            {emps.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
          </select>
          <select value={reviewForm.review_cycle} onChange={e => setReviewForm({ ...reviewForm, review_cycle: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            {['ANNUAL', 'SEMI_ANNUAL', 'QUARTERLY'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Period Start</label><input type="date" value={reviewForm.review_period_start} onChange={e => setReviewForm({ ...reviewForm, review_period_start: e.target.value })} style={inputStyle} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Period End</label><input type="date" value={reviewForm.review_period_end} onChange={e => setReviewForm({ ...reviewForm, review_period_end: e.target.value })} style={inputStyle} /></div>
          </div>
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
